import { useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { useElements, useStripe } from "@stripe/react-stripe-js"
import {
  captureWidgetOrderPayment,
  createWidgetOrder,
  createWidgetOrderPayment,
  fetchWidgetCart,
  fetchWidgetOrder,
  updateWidgetOrder
} from "store/experiences"
import { capturePayPalOrder, createPayPalOrder } from "store/payment-provider"
import { CART_ERRORS, ORDER_CHARGE_TOLERANCE, ORDER_ERRORS, ORDER_STATUSES } from "../constants"
import { buildOrderPayload, buildOrderUpdatePayload, getOrderChargeNow, getOrderError } from "./payment"
import { createGuestCard } from "./cardPayment"
import { forgetPendingOrder, readPendingOrder, savePendingOrder } from "./pendingOrderStorage"

// A payment intent the card has already authorized (an earlier attempt got this far): capture it
// instead of confirming the card again.
const AUTHORIZED_INTENT_STATUSES = new Set(["requires_capture", "succeeded", "processing"])

const isSameCartIds = (cartIds, items) => cartIds?.length === items.length && items.every((item) => cartIds.includes(item.cartId))

// Pays the cart's several items as ONE order (POST /orders, one booking per item, all or nothing),
// with one card, wallet or PayPal payment. The order is made when the guest pays; its amounts are
// the server's, and when today's charge isn't what the Pay button showed nothing is charged:
// onPriceChanged gets that charge for the guest to confirm. A booking within 24 hours is a request
// the guide accepts: by card it is charged on acceptance, by PayPal now (`isChargedOnApproval`).
// The order waiting for its payment is kept (pendingOrderStorage), so a declined card, a reload or
// another tab carries on paying the same order: the API refuses a second order for the same holds.
//  - isSubmittingRef blocks a second Pay while one runs, and stays locked once the card may have
//    been charged.
//  - onExpired: the order went unpaid for 15 minutes (or the cart's holds ran out) and was dropped.
//  - onItemError: the API refused one item ({ key, message, cartId }); nothing was charged.
//  - onPaid: the paid order with each booking's details, for the success screen.
const useWidgetOrderCheckout = ({ guideUuid, guideId, cartToken, items, isEnabled, onPaid, onExpired, onItemError, onPriceChanged }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const stripe = useStripe()
  const elements = useElements()
  const [isPaying, setIsPaying] = useState(false)
  const [paymentError, setPaymentError] = useState(null)
  const isSubmittingRef = useRef(false)
  // The order waiting for its payment: the API's order plus its guest_token.
  const orderRef = useRef(null)
  // Set when the last PayPal attempt already said what went wrong, so PayPal's own error is skipped.
  const isPayPalErrorShownRef = useRef(false)
  const latestRef = useRef(null)
  latestRef.current = { items, cartToken, onPaid, onExpired }

  const forgetOrder = () => {
    orderRef.current = null
    forgetPendingOrder(guideUuid)
  }

  // The order with each booking's details (waivers, questionnaire, client): GET /orders/:uuid.
  const readOrder = async (order) => ({ ...(await dispatch(fetchWidgetOrder(order.uuid, order.guest_token))), guest_token: order.guest_token })

  const finishPaid = async (order) => {
    let paidOrder = order
    try {
      paidOrder = await readOrder(order)
    } catch (error) {
      // The order is paid either way; the success screen shows what the payment answered.
    }
    forgetOrder()
    // The success screen's waiver and questionnaire calls use the guest's token.
    localStorage.setItem("guest_token", order.guest_token)
    latestRef.current.onPaid(paidOrder)
  }

  // A reload or another tab: carry on paying the order this cart already made, show it paid, or drop
  // it once it can't be paid any more.
  useEffect(() => {
    if (!isEnabled || orderRef.current) return
    const saved = readPendingOrder(guideUuid)
    if (!saved) return
    if (saved.cartToken !== latestRef.current.cartToken) {
      forgetPendingOrder(guideUuid)
      return
    }
    dispatch(fetchWidgetOrder(saved.uuid, saved.guestToken))
      .then((order) => {
        const known = { ...order, guest_token: saved.guestToken, cartIds: saved.cartIds }
        if (order?.status === ORDER_STATUSES.paid) return finishPaid(known)
        if (order?.status === ORDER_STATUSES.pendingPayment) {
          orderRef.current = known
          return null
        }
        forgetPendingOrder(guideUuid)
        if (order?.status === ORDER_STATUSES.expired) latestRef.current.onExpired()
        return null
      })
      .catch(() => forgetPendingOrder(guideUuid))
  }, [isEnabled, guideUuid]) // eslint-disable-line react-hooks/exhaustive-deps

  // What a refused order call means for the guest. Returns true when it was shown.
  const showOrderError = (error) => {
    const orderError = getOrderError(error)
    if (orderError.key === ORDER_ERRORS.orderExpired || orderError.key === CART_ERRORS.holdExpired) {
      forgetOrder()
      dispatch(fetchWidgetCart())
      setPaymentError(null)
      onExpired()
      return true
    }
    if (orderError.cartId) {
      setPaymentError(null)
      onItemError(orderError)
      return true
    }
    setPaymentError(orderError.message || t("booking_widget.checkout.errors.generic"))
    return true
  }

  // The order to pay: the one already made for this cart (brought up to date with the guest's
  // latest choices), or a new one. Null when today's charge isn't what the guest was shown. An order
  // that ran out while the guest waited is replaced by a new one: "Check availability & continue"
  // has reserved the cart again by then (if not, the new order is refused, hold_expired).
  const ensureOrder = async (details) => {
    const { values, paymentOption, promoCode, expectedChargeNow, isChargedOnApproval } = details
    const existing = orderRef.current
    let order
    if (existing) {
      if (!isSameCartIds(existing.cartIds, items)) {
        setPaymentError(t("booking_widget.checkout.errors.order_in_progress"))
        return null
      }
      let updated
      try {
        updated = await dispatch(updateWidgetOrder(existing.uuid, existing.guest_token, buildOrderUpdatePayload({ values, paymentOption, promoCode })))
      } catch (error) {
        if (getOrderError(error).key !== ORDER_ERRORS.orderExpired) throw error
        forgetOrder()
        return ensureOrder(details)
      }
      // The update's amounts, with each booking's details kept from the full order
      const detailsById = Object.fromEntries((existing.bookings || []).map((booking) => [booking.client_item_id, booking.details]))
      const bookings = (updated?.bookings || existing.bookings).map((booking) => ({ ...booking, details: booking.details || detailsById[booking.client_item_id] }))
      order = { ...existing, ...updated, bookings }
    } else {
      const created = await dispatch(createWidgetOrder(buildOrderPayload({ items, values, paymentOption, promoCode })))
      const cartIds = items.map((item) => item.cartId)
      savePendingOrder(guideUuid, { uuid: created.uuid, guestToken: created.guest_token, cartToken, cartIds })
      // Each booking's details (the guest's client id for the card) come with the full order.
      order = { ...(await readOrder(created)), cartIds }
      // The cart's items now belong to the order: its timer stops showing.
      dispatch(fetchWidgetCart())
    }
    orderRef.current = order
    const chargeNow = getOrderChargeNow(order, isChargedOnApproval)
    if (Math.abs(chargeNow - expectedChargeNow) > ORDER_CHARGE_TOLERANCE + Number.EPSILON) {
      onPriceChanged(chargeNow)
      return null
    }
    return order
  }

  // The card is charged by capturing the authorized intent. Once capture has answered, the money may
  // have been taken: a failure then keeps Pay locked and asks the guest to contact support.
  const capture = async (order, paymentIntentId) => {
    let paid = null
    try {
      paid = await dispatch(captureWidgetOrderPayment(order.uuid, order.guest_token, paymentIntentId))
    } catch (error) {
      const orderError = getOrderError(error)
      // A booking can't be honoured any more: the authorization was cancelled, nothing was charged.
      if (orderError.cartId || orderError.key === ORDER_ERRORS.paymentNotAuthorized) {
        isSubmittingRef.current = false
        showOrderError(error)
        return
      }
      setPaymentError(t("booking_widget.checkout.errors.capture"))
      return
    }
    if (paid?.status !== ORDER_STATUSES.paid) {
      setPaymentError(t("booking_widget.checkout.errors.capture"))
      return
    }
    await finishPaid({ ...order, ...paid })
  }

  // The order's payment intent, or what to do instead: { intent } to confirm, { isPaid } when
  // nothing is left to pay now (e.g. a 100% code), or null after showing why it can't be paid.
  const getPaymentIntent = async (order) => {
    const intent = await dispatch(createWidgetOrderPayment(order.uuid, order.guest_token))
    if (intent?.card_payments_available === false) {
      setPaymentError(intent.message || t("booking_widget.checkout.errors.generic"))
      return null
    }
    if (!intent?.client_secret) {
      if (intent?.status === "succeeded") return { isPaid: true }
      setPaymentError(t("booking_widget.checkout.errors.generic"))
      return null
    }
    return { intent }
  }

  const startPaying = () => {
    if (isSubmittingRef.current) return false
    isSubmittingRef.current = true
    setIsPaying(true)
    setPaymentError(null)
    return true
  }

  // By card and wallets a request is charged when the guide accepts (isChargedOnApproval: true); by
  // PayPal everything is charged now.
  // Confirm and Pay with the card fields. The card is saved to the guest first (a balance paid later
  // is charged to it), then the whole order is authorized and captured.
  const payByCard = async ({ values, paymentOption, promoCode, expectedChargeNow }) => {
    if (!stripe || !elements || !startPaying()) return
    let isCharging = false
    try {
      const order = await ensureOrder({ values, paymentOption, promoCode, expectedChargeNow, isChargedOnApproval: true })
      if (!order) return
      const clientId = order.bookings?.[0]?.details?.client?.id
      const { paymentMethodId, error: cardError } = await createGuestCard({ stripe, elements, dispatch, values, clientId, guideId, guestToken: order.guest_token })
      if (!paymentMethodId) {
        setPaymentError(cardError || t("booking_widget.checkout.errors.card"))
        return
      }
      const payment = await getPaymentIntent(order)
      if (!payment) return
      isCharging = true
      if (payment.isPaid) {
        await finishPaid(order)
        return
      }
      const { intent } = payment
      // A retry after the connection dropped gets the same intent back: if the card was already
      // authorized, capture it rather than confirming the card again.
      const { paymentIntent: existing } = await stripe.retrievePaymentIntent(intent.client_secret)
      if (!AUTHORIZED_INTENT_STATUSES.has(existing?.status)) {
        const result = await stripe.confirmCardPayment(intent.client_secret, { payment_method: paymentMethodId })
        if (result?.error) {
          isCharging = false
          setPaymentError(result.error.message)
          return
        }
      }
      await capture(order, intent.id)
    } catch (error) {
      isCharging = false
      showOrderError(error)
    } finally {
      setIsPaying(false)
      if (!isCharging) isSubmittingRef.current = false
    }
  }

  // Apple / Google Pay (the total today only): the wallet sheet hands over a payment method.
  const payByWallet = async (event, { values, paymentOption, promoCode, expectedChargeNow }) => {
    if (!startPaying()) {
      event.complete("fail")
      return
    }
    let isCharging = false
    try {
      const order = await ensureOrder({ values, paymentOption, promoCode, expectedChargeNow, isChargedOnApproval: true })
      const payment = order && (await getPaymentIntent(order))
      if (!payment) {
        event.complete("fail")
        return
      }
      isCharging = true
      if (payment.isPaid) {
        event.complete("success")
        await finishPaid(order)
        return
      }
      const { intent } = payment
      const { paymentIntent, error } = await stripe.confirmCardPayment(intent.client_secret, { payment_method: event.paymentMethod.id }, { handleActions: false })
      if (error) {
        isCharging = false
        event.complete("fail")
        setPaymentError(error.message)
        return
      }
      event.complete("success")
      if (paymentIntent?.status === "requires_action") {
        const result = await stripe.confirmCardPayment(intent.client_secret)
        if (result?.error) {
          isCharging = false
          setPaymentError(result.error.message)
          return
        }
      }
      await capture(order, intent.id)
    } catch (error) {
      isCharging = false
      event.complete("fail")
      showOrderError(error)
    } finally {
      setIsPaying(false)
      if (!isCharging) isSubmittingRef.current = false
    }
  }

  // PayPal: the order is made (or brought up to date) when the guest opens the PayPal window, then
  // captured on approval. A refusal is shown here, and PayPal's own error is then skipped.
  const createPayPalOrderForBooking = async ({ values, paymentOption, promoCode, expectedChargeNow }) => {
    setPaymentError(null)
    isPayPalErrorShownRef.current = false
    try {
      const order = await ensureOrder({ values, paymentOption, promoCode, expectedChargeNow, isChargedOnApproval: false })
      if (!order) {
        isPayPalErrorShownRef.current = true
        throw new Error("order_not_payable")
      }
      const response = await dispatch(createPayPalOrder({ order_uuid: order.uuid }, { guestToken: order.guest_token }))
      return response?.order_id
    } catch (error) {
      if (error?.data) isPayPalErrorShownRef.current = showOrderError(error)
      throw error
    }
  }

  const handlePayPalError = () => {
    if (!isPayPalErrorShownRef.current) setPaymentError(t("booking_widget.checkout.errors.paypal"))
  }

  const approvePayPalOrder = async (payPalOrderId) => {
    const order = orderRef.current
    if (!payPalOrderId || !order) return
    setIsPaying(true)
    isSubmittingRef.current = true
    try {
      const response = await dispatch(capturePayPalOrder({ order_uuid: order.uuid, order_id: payPalOrderId }, { guestToken: order.guest_token }))
      if (response?.success) await finishPaid({ ...order, ...response.order })
      else setPaymentError(t("booking_widget.checkout.errors.paypal"))
    } catch (error) {
      isSubmittingRef.current = false
      showOrderError(error)
    } finally {
      setIsPaying(false)
    }
  }

  return {
    isPaying,
    paymentError,
    setPaymentError,
    payByCard,
    payByWallet,
    createPayPalOrderForBooking,
    approvePayPalOrder,
    handlePayPalError
  }
}

export default useWidgetOrderCheckout
